import React, { useEffect, useMemo, useState } from "react";
import type { TooltipProps } from "recharts";
import type { ScatterEvent, TypeMeta } from "../EventScatterShape";
import { resolveTypeMeta } from "../EventScatterShape";
import { useTheme } from "@mui/material/styles";
import { ThemeProps } from "@styles/theme";

function tryFormatDateTime(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) {return value;}

    const parsedMs = Date.parse(trimmed);
    if (!Number.isFinite(parsedMs)) {return value;}

    if (!/[t:\-/]/i.test(trimmed)) {return value;}

    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).format(new Date(parsedMs));
}

function formatDisplayValue(rawValue: unknown): string {
    if (rawValue == null) {return '—';}
    if (typeof rawValue === 'string') {return tryFormatDateTime(rawValue);}
    if (typeof rawValue === 'number' || typeof rawValue === 'boolean') {return String(rawValue);}

    try {
        return JSON.stringify(rawValue);
    } catch {
        return '—';
    }
}

function toEntries(value: unknown): Array<{ key: string; value: string }> {
    if (value == null) {return [];}

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return [{ key: 'Value', value: formatDisplayValue(value) }];
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
        return Object.entries(value as Record<string, unknown>)
            .filter(([key]) => !key.startsWith('_'))
            .map(([key, rawValue]) => ({
                key,
                value: formatDisplayValue(rawValue),
            }));
    }

    try {
        return [{ key: 'Value', value: formatDisplayValue(value) }];
    } catch {
        return [];
    }
}

function nicifyKeyLabel(value: string): string {
    return value
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .trim();
}

export function EventScatterTooltip({
    active,
    payload,
    typeMeta,
    fallbackTypeMeta,
    event,
}: TooltipProps<number, string> & {
  typeMeta?: Record<string, TypeMeta>;
  fallbackTypeMeta?: TypeMeta;
  event?: ScatterEvent | null;
}) {
    const hoveredPayload = payload?.[0]?.payload as ScatterEvent | undefined;
    const p = event ?? hoveredPayload;
    const theme = useTheme<ThemeProps>();
    const c = theme.colors;

    if (!p) {return null;}
    if (!event && (!active || !payload?.length)) {return null;}

    if (!p) {return null;}

    const meta = resolveTypeMeta(p.type, typeMeta, fallbackTypeMeta);
    const title = (p.label ?? meta.label).replace(/([A-Z])/g, ' $1').trim();
    const isMerged = (p.mergedCount ?? 1) > 1;
    const mergedItems = useMemo(() => p.mergedItems ?? [], [p.mergedItems]);
    const [selectedMergedIndex, setSelectedMergedIndex] = useState(0);

    useEffect(() => {
        setSelectedMergedIndex(0);
    }, [p.time, p.type, p.label, mergedItems.length]);

    const selectedMergedItem = isMerged && event
        ? mergedItems[Math.min(selectedMergedIndex, Math.max(mergedItems.length - 1, 0))] ?? p
        : p;

    const valueEntries = event ? toEntries(selectedMergedItem.value) : [];
    const selectedMeta = resolveTypeMeta(selectedMergedItem.type, typeMeta, fallbackTypeMeta);
    const selectedTitle = (selectedMergedItem.label ?? selectedMeta.label).replace(/([A-Z])/g, ' $1').trim();
    const selectedDescription = selectedMergedItem.description;

    return (
        <div
            style={{
                background: isMerged ? c.surface_elevated : c.white,
                borderRadius: 10,
                padding: "10px 12px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                border: isMerged ? `1px solid ${meta.color}` : "1px solid rgba(0,0,0,0.08)",
                minWidth: 170,
                maxWidth: 320,
                pointerEvents: event ? 'auto' : 'none',
            }}
        >
            <div style={{ fontWeight: 700, color: meta.color, marginBottom: 4 }}>
                {title}
            </div>
            {isMerged ? (
                <div style={{ fontSize: 12, fontWeight: 700, color: meta.color, marginBottom: 6 }}>
          Merged event cluster: {p.mergedCount}
                </div>
            ) : null}
            <div style={{ fontSize: 13, color: c.text_heading }}>{p.description}</div>
            {event && isMerged && mergedItems.length > 1 ? (
                <>
                    <div
                        style={{
                            marginTop: 10,
                            display: 'flex',
                            gap: 6,
                            overflowX: 'auto',
                            paddingBottom: 4,
                        }}
                    >
                        {mergedItems.map((item, index) => {
                            const itemMeta = resolveTypeMeta(item.type, typeMeta, fallbackTypeMeta);
                            const isActiveTab = index === selectedMergedIndex;

                            return (
                                <button
                                    key={`${item.type}-${item.time}-${index}`}
                                    type="button"
                                    onClick={() => setSelectedMergedIndex(index)}
                                    style={{
                                        border: `1px solid ${isActiveTab ? itemMeta.color : 'rgba(0,0,0,0.12)'}`,
                                        background: isActiveTab ? `${itemMeta.color}14` : c.white,
                                        color: isActiveTab ? itemMeta.color : c.text_heading,
                                        borderRadius: 999,
                                        padding: '5px 10px',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        flex: '0 0 auto',
                                    }}
                                >
                                    {item.label ? `${index + 1}. ${item.label}` : `Event ${index + 1}`}
                                </button>
                            );
                        })}
                    </div>
                    <div
                        style={{
                            marginTop: 6,
                            borderTop: '1px solid rgba(0,0,0,0.08)',
                            paddingTop: 10,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            maxHeight: 280,
                            overflowY: 'auto',
                        }}
                    >
                        <div
                            style={{
                                borderRadius: 8,
                                background: c.white,
                                border: `1px solid ${selectedMeta.color}33`,
                                padding: '8px 10px',
                            }}
                        >
                            <div style={{ fontSize: 11, fontWeight: 700, color: selectedMeta.color, marginBottom: 4 }}>
                                {selectedTitle}
                            </div>
                            <div style={{ fontSize: 13, color: c.text_heading }}>{selectedDescription}</div>
                        </div>
                        {valueEntries.length ? (
                            valueEntries.map(entry => (
                                <div
                                    key={`${selectedMergedIndex}-${entry.key}`}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 4,
                                        padding: '8px 10px',
                                        borderRadius: 8,
                                        background: c.surface_page,
                                        border: '1px solid rgba(0,0,0,0.06)',
                                    }}
                                >
                                    <div
                                        style={{
                                            alignSelf: 'flex-start',
                                            fontSize: 11,
                                            lineHeight: 1.2,
                                            fontWeight: 700,
                                            color: c.text_subtle_purple,
                                        }}
                                    >
                                        {nicifyKeyLabel(entry.key)}
                                    </div>
                                    <div style={{ fontSize: 13, color: c.text_heading, wordBreak: 'break-word' }}>{entry.value}</div>
                                </div>
                            ))
                        ) : (
                            <div style={{ fontSize: 12, color: c.grey_text, fontStyle: 'italic' }}>No extra value fields</div>
                        )}
                    </div>
                </>
            ) : null}
            {!isMerged && valueEntries.length ? (
                <div
                    style={{
                        marginTop: 8,
                        borderTop: "1px solid rgba(0,0,0,0.08)",
                        paddingTop: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                    }}
                >
                    {valueEntries.map(entry => (
                        <div
                            key={entry.key}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 4,
                                padding: '8px 10px',
                                borderRadius: 8,
                                background: c.surface_page,
                                border: '1px solid rgba(0,0,0,0.06)',
                            }}
                        >
                            <div
                                style={{
                                    alignSelf: 'flex-start',
                                    fontSize: 11,
                                    lineHeight: 1.2,
                                    fontWeight: 700,
                                    color: c.text_subtle_purple,
                                }}
                            >
                                {nicifyKeyLabel(entry.key)}
                            </div>
                            <div style={{ fontSize: 13, color: c.text_heading, wordBreak: 'break-word' }}>{entry.value}</div>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

type CrosshairCursorProps = {
  viewBox?: { x: number; y: number; width: number; height: number };

  x?: number;
  y?: number;
  points?: Array<{ x: number; y: number }>;
  left?: number;
  top?: number;
  width?: number;
  height?: number;

  payload?: Array<{ payload?: ScatterEvent }>;

  timeFormatter?: (v: number) => string;
  lineDash?: string;

  typeMeta?: Record<string, TypeMeta>;
  fallbackTypeMeta?: TypeMeta;

  badgeTextColor?: string;
  badgeFill?: string;
  lineColor?: string;
};

export function CrosshairCursor({
    viewBox,
    x,
    y,
    left,
    top,
    width,
    height,
    payload,
    timeFormatter = v => String(v),
    lineDash = "4 4",
    typeMeta,
    fallbackTypeMeta,
    badgeTextColor = "#fff",
    badgeFill,
    lineColor,
}: CrosshairCursorProps) {
    if (x == null || y == null) {return null;}

    const plotLeft = viewBox?.x ?? left ?? 0;
    const plotTop = viewBox?.y ?? top ?? 0;
    const plotWidth = viewBox?.width ?? width ?? 0;
    const plotHeight = viewBox?.height ?? height ?? 0;

    const hovered = payload?.[0]?.payload as ScatterEvent | undefined;
    const meta = hovered
        ? resolveTypeMeta(hovered.type, typeMeta, fallbackTypeMeta)
        : fallbackTypeMeta;

    const resolvedColor = lineColor ?? meta?.color ?? "#7b61ff";
    const resolvedBadgeFill = badgeFill ?? resolvedColor;

    const timeValue = hovered?.time;
    const timeText =
    typeof timeValue === "number" ? timeFormatter(timeValue) : "";

    const fontSize = 11;
    const padX = 6;
    const badgeH = 16;
    const textW = Math.max(20, timeText.length * 6.5);
    const badgeW = textW + padX * 2;

    const xMin = plotLeft;
    const xMax = plotLeft + plotWidth;
    const badgeX = Math.min(Math.max(x - badgeW / 2, xMin), xMax - badgeW);
    const badgeY = plotTop + 2;

    return (
        <g pointerEvents="none">
            <line
                x1={x}
                y1={plotTop}
                x2={x}
                y2={plotTop + plotHeight}
                stroke={resolvedColor}
                strokeWidth={1}
            />

            <line
                x1={plotLeft}
                y1={y}
                x2={plotLeft + plotWidth}
                y2={y}
                stroke={resolvedColor}
                strokeWidth={1}
                strokeDasharray={lineDash}
            />

            {timeText ? (
                <>
                    <rect
                        x={badgeX}
                        y={badgeY}
                        rx={4}
                        ry={4}
                        width={badgeW}
                        height={badgeH}
                        fill={resolvedBadgeFill}
                    />
                    <text
                        x={badgeX + badgeW / 2}
                        y={badgeY + badgeH / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill={badgeTextColor}
                        fontSize={fontSize}
                        fontWeight={700}
                    >
                        {timeText}
                    </text>
                </>
            ) : null}
        </g>
    );
}